import { useState } from "react"
import { Alert, Button, Collapse, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { Modal } from "@core/components/modal"
import type { SlotOccurrence } from "@core/components/telegram/types"
import {
  fromVersionContent,
  type TemplateWriteAccess,
} from "../../hooks/useVersionDraft"
import type {
  Bindings,
  ReadBindings,
  TemplateVersion,
} from "../../interface/publications"
import {
  bindingUrl,
  getActiveBindingSlots,
  getMissingActiveBindings,
} from "../../utils/bindings"
import { BindingValueForm } from "./bindingValueForm"
import { OrderLinkPicker } from "./orderLinkPicker"

const locationLabel = (occurrence: SlotOccurrence) => {
  const parts = occurrence.path.split("/")
  if (occurrence.document === "entity-url")
    return `Текст, ссылка ${Number(parts[3]) + 1}`
  return occurrence.document.startsWith("button")
    ? `Кнопка ${Number(parts[2]) + 1}.${Number(parts[3]) + 1}, ${occurrence.document === "button-text" ? "текст" : "ссылка"}`
    : occurrence.offset === undefined
      ? "Текст, ссылка"
      : `Текст, позиция ${occurrence.offset + 1}`
}
const valueLabel = (value: ReadBindings[string] | undefined) =>
  value?.botId !== undefined
    ? `Сохранённый botId=${value.botId}; изменение требует явного преобразования`
    : (bindingUrl(value) ?? "Не заполнено")

export interface PublicationBindingsProps extends TemplateWriteAccess {
  version: TemplateVersion
  bindings: ReadBindings
  onChange: (replacements: Bindings) => void
  selected?: string
  onSelect: (slotId?: string) => void
  onCreateVersion?: () => void
}
export function PublicationBindings({
  version,
  bindings,
  onChange,
  readOnly,
  canWrite,
  selected,
  onSelect: setSelected,
  onCreateVersion,
}: PublicationBindingsProps) {
  const content = fromVersionContent(version.content)
  const groups = getActiveBindingSlots(content)
  const inactive = Object.keys(bindings).filter(
    (id) => !groups.some(({ slotId }) => slotId === id),
  )
  const [inactiveOpen, setInactiveOpen] = useState(false)
  const [orderOpen, setOrderOpen] = useState(false)
  const editable = !readOnly && canWrite !== false
  const active = groups.find(({ slotId }) => slotId === selected)
  const missing = getMissingActiveBindings(content, bindings)
  const legacy = groups.filter(
    ({ slotId }) => bindings[slotId]?.botId !== undefined,
  )
  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2">Ссылки публикации</Typography>
      {!!missing.length && (
        <Alert severity="warning">
          Не заполнено: {missing.length}. Укажите значения активных подстановок.
        </Alert>
      )}
      {!!legacy.length && (
        <Alert severity="warning">
          Сохранены legacy botId: {legacy.length}. Неизменённые значения можно
          оставить. Для изменения выберите URL или бота.
        </Alert>
      )}
      {!groups.length && (
        <Typography variant="body2" color="text.secondary">
          В этой версии нет мест для подстановки
        </Typography>
      )}
      {groups.map(({ slotId, label, occurrences }) => (
        <Stack
          key={slotId}
          spacing={0.5}
          sx={{
            border: 1,
            borderColor: (theme) => alpha(theme.palette.text.primary, 0.14),
            borderRadius: 1,
            p: 1,
            minWidth: 0,
          }}
        >
          <Typography variant="subtitle2" sx={{ overflowWrap: "anywhere" }}>
            {label}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ overflowWrap: "anywhere" }}
          >
            {valueLabel(bindings[slotId])}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Используется в {occurrences.length}{" "}
            {occurrences.length % 10 === 1 && occurrences.length % 100 !== 11
              ? "месте"
              : "местах"}
          </Typography>
          <Button
            size="small"
            aria-label={`${editable ? "Изменить" : "Просмотреть"}: ${label}`}
            sx={{ alignSelf: "flex-start" }}
            onClick={() => setSelected(slotId)}
          >
            {editable ? "Изменить" : "Просмотреть"}
          </Button>
        </Stack>
      ))}
      {!!inactive.length && (
        <>
          <Button
            size="small"
            sx={{ alignSelf: "flex-start" }}
            aria-expanded={inactiveOpen}
            onClick={() => setInactiveOpen(!inactiveOpen)}
          >
            Неактивные значения ({inactive.length})
          </Button>
          <Collapse in={inactiveOpen} unmountOnExit>
            {inactive.map((id, index) => (
              <Stack key={id}>
                <Typography>{valueLabel(bindings[id])}</Typography>
                <Button onClick={() => setSelected(id)}>
                  {editable ? "Изменить" : "Просмотреть"} неактивное значение{" "}
                  {index + 1}
                </Button>
              </Stack>
            ))}
          </Collapse>
        </>
      )}
      {editable && (
        <Button onClick={() => setOrderOpen(true)}>Ссылки из ордера ADV</Button>
      )}
      <OrderLinkPicker
        open={orderOpen}
        onClose={() => setOrderOpen(false)}
        content={content}
        bindings={bindings}
        disabled={!editable}
        onCreateVersion={onCreateVersion}
        onApply={(values) => {
          if (editable) onChange(values)
        }}
      />
      {selected && (
        <Modal
          title={active?.label ?? "Неактивное значение"}
          onClose={() => setSelected(undefined)}
        >
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary">
              Использований: {active?.occurrences.length ?? 0}
            </Typography>
            <Stack
              component="ul"
              sx={{
                m: 0,
                pl: 2,
                typography: "caption",
                maxHeight: 100,
                overflow: "auto",
              }}
            >
              {active?.occurrences.map((occurrence, index) => (
                <li key={index}>
                  {occurrence.label || "Без подписи"} —{" "}
                  {locationLabel(occurrence)}
                </li>
              ))}
            </Stack>
            {!active && (
              <Typography>В выбранной версии вхождений нет.</Typography>
            )}
            <BindingValueForm
              value={bindings[selected]}
              disabled={!editable}
              onCancel={() => setSelected(undefined)}
              onApply={(value) => {
                if (!editable) return
                onChange({ [selected]: value })
                setSelected(undefined)
              }}
            />
          </Stack>
        </Modal>
      )}
    </Stack>
  )
}
